import React, { useEffect, useRef, useState, useCallback } from 'react';
import Slider from 'react-slick';
import { Icon } from '../../../Universal/NovusDSImports';
import { TabItems } from '../../globalStyles';
import { STRINGS } from '../../../Constants/ConstantStrings';
import { FilterBadge } from './styles';
import { TooltipComponent } from '../../../SharedComponets/Tooltip';

interface RegisteredUsersCarouselProps {
  vehicleStats: Record<string, any>;
  isSideNav: boolean;
  activeTabName: string | null;
  onFilterClick: (filter: string, abbrName?: string) => void;
  onClear: () => void;
}

const PrevArrow: React.FC<any> = ({
  className,
  style,
  onClick,
  currentSlide,
  isvisible
}) => {
  if (!isvisible) return <></>;
  return (
    <>
      <div
        data-testid="previous-arrow"
        data-tooltip-id="previous-arrow-tooltip"
        role="button"
        className={`${className} ${currentSlide === 0 ? 'disabled' : ''}`}
        style={{ ...style }}
        onClick={currentSlide !== 0 ? onClick : undefined}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && currentSlide !== 0) {
            onClick();
          }
        }}
        aria-label={'previous arrow'}
      >
        <Icon icon="chevron_left_double" />
      </div>
      <TooltipComponent id={'previous-arrow-tooltip'} noArrow place="bottom">
        {'Scroll to view additional items'}
      </TooltipComponent>
    </>
  );
};

const NextArrow: React.FC<any> = ({ className, style, onClick, isvisible }) => {
  if (!isvisible) return <></>;
  return (
    <>
      <div
        role="button"
        data-testid="next-arrow"
        data-tooltip-id="next-arrow-tooltip"
        className={`${className}`}
        style={{ ...style }}
        onClick={onClick}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            onClick();
          }
        }}
        aria-label={'next arrow'}
      >
        <Icon icon="chevron_right_double" />
      </div>
      <TooltipComponent id={'next-arrow-tooltip'} noArrow place="bottom">
        {'Scroll to view additional items'}
      </TooltipComponent>
    </>
  );
};

const RegisteredUsersCarousel: React.FC<RegisteredUsersCarouselProps> = ({
  vehicleStats,
  activeTabName,
  onFilterClick,
  onClear
}) => {
  const flatFilters = React.useMemo(() => {
    if (!vehicleStats) return [] as Array<{ abbr: string; filter: string }>;
    const arr: Array<{ abbr: string; filter: string }> = [];
    Object.keys(vehicleStats).forEach((abbr) => {
      Object.keys(vehicleStats[abbr]).forEach((filter) => {
        arr.push({ abbr, filter });
      });
    });
    return arr;
  }, [vehicleStats]);

  const sliderRef = useRef<Slider | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [dynamicSlidesToShow, setDynamicSlidesToShow] = useState<number>(4);

  const MIN_TAB_WIDTH = 150;

  const computeSlidesToShow = useCallback(() => {
    // Determine container width and calculate how many tabs of min width can fit.
    const container = sliderRef.current?.innerSlider?.list as
      | HTMLElement
      | undefined;
    const width = container?.clientWidth || 0;
    if (!width) {
      setDynamicSlidesToShow(4); // fallback
      return;
    }
    const possible = Math.max(1, Math.floor(width / MIN_TAB_WIDTH));
    // Never exceed total number of filters
    const total = flatFilters.length || 1;
    setDynamicSlidesToShow(Math.min(possible, total));
  }, [flatFilters.length]);

  const computeVisibleSlides = useCallback(() => {
    const total = flatFilters.length;
    setShowPrev(currentSlide > 0);
    setShowNext(currentSlide + dynamicSlidesToShow < total);
  }, [currentSlide, dynamicSlidesToShow, flatFilters.length]);

  useEffect(() => {
    computeSlidesToShow();
  }, [computeSlidesToShow]);

  useEffect(() => {
    computeVisibleSlides();
  }, [computeVisibleSlides, dynamicSlidesToShow]);

  useEffect(() => {
    const handleResize = () => {
      computeSlidesToShow();
      // after recalculating slidesToShow also recompute arrows
      computeVisibleSlides();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [computeSlidesToShow, computeVisibleSlides]);

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: dynamicSlidesToShow,
    slidesToScroll: 1,
    prevArrow: <PrevArrow isvisible={showPrev} className="arrow-button" />,
    nextArrow: <NextArrow isvisible={showNext} className="arrow-button" />,
    beforeChange: (_: number, next: number) => setCurrentSlide(next),
    responsive: [
      {
        breakpoint: 1920,
        settings: { slidesToShow: dynamicSlidesToShow, slidesToScroll: 1 }
      },
      {
        breakpoint: 1500,
        settings: { slidesToShow: dynamicSlidesToShow, slidesToScroll: 1 }
      },
      {
        breakpoint: 1366,
        settings: { slidesToShow: dynamicSlidesToShow, slidesToScroll: 1 }
      },
      {
        breakpoint: 1024,
        settings: { slidesToShow: dynamicSlidesToShow, slidesToScroll: 1 }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: Math.min(dynamicSlidesToShow, 2),
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 480,
        settings: { slidesToShow: 1, slidesToScroll: 1 }
      }
    ]
  };

  useEffect(() => {
    const slick = document.querySelector('.slick-track');
    const slickSlide = document.querySelectorAll('.slick-slide');
    if (slick) slick.setAttribute('role', 'tablist');
    slickSlide.forEach((slide) => {
      slide.setAttribute('role', 'tab');
    });
  }, [vehicleStats]);

  return (
    <div className="left-wrapper">
      <div
        data-testid="slider-container"
        className="slider-container"
        tabIndex={0}
        aria-label={'tablist'}
      >
        <Slider {...settings} ref={sliderRef} tabIndex={0}>
          {flatFilters.map(({ abbr, filter }, index) => {
            const slidesShowing = dynamicSlidesToShow || 1;
            const isHidden =
              index < currentSlide || index >= currentSlide + slidesShowing;
            return (
              <div
                key={`${abbr}-${filter}-${index}`}
                style={{ minWidth: MIN_TAB_WIDTH }}
                className="d-flex"
              >
                <TabItems
                  data-testid={`tab-${filter}`}
                  id={`tab-${filter}`}
                  data-tooltip-id={`tooltip-${filter}`}
                  tabIndex={isHidden ? -1 : 0}
                  aria-hidden={isHidden ? 'true' : undefined}
                  onClick={() => {
                    if (isHidden) return;
                    if (activeTabName !== filter) {
                      onFilterClick(filter, abbr);
                    } else {
                      onClear();
                    }
                  }}
                  selectedtab={activeTabName === filter}
                  aria-label={filter}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !isHidden) {
                      if (activeTabName !== filter) {
                        onFilterClick(filter, abbr);
                      } else {
                        onClear();
                      }
                    }
                  }}
                  title={`${
                    filter === 'not_yet_contacted'
                      ? 'Newly Added (Not Yet Contacted)'
                      : filter
                  } ${STRINGS.ONLY}`}
                  className="justify-content-between w-100"
                >
                  <span className="text-truncate">
                    {filter === 'not_yet_contacted'
                      ? 'Newly Added (Not Yet Contacted)'
                      : filter}{' '}
                    {STRINGS.ONLY}
                  </span>
                  <FilterBadge filterName={filter}>
                    {vehicleStats?.[abbr]?.[filter]}
                  </FilterBadge>
                </TabItems>
              </div>
            );
          })}
        </Slider>
      </div>
    </div>
  );
};

export default RegisteredUsersCarousel;
